import { Stack, Typography, useTheme } from "@mui/material";
import { WeeklySales } from "@domain/sales/models/Sales";
import { useMemo } from "react";
import { Area, Tooltip, YAxis, CartesianGrid, ComposedChart, ResponsiveContainer } from "recharts";
import { getISOWeek, getISOWeekYear, subWeeks } from "date-fns";
import { addTransparency } from "../../../../application/styles/colorStyles";

interface ServiceLevelProps {
    weeklySales: WeeklySales[];
}

export const ServiceLevel = ({ weeklySales }: ServiceLevelProps) => {
    const theme = useTheme();


    const weeklyServiceLevel = useMemo(() => {

        const currentWeek = getISOWeek(new Date());
        const currentYear = getISOWeekYear(new Date());

        const firstWeek = getISOWeek(subWeeks(new Date(), 52))
        const firstYear = getISOWeekYear(subWeeks(new Date(), 52));

        weeklySales = weeklySales.filter(week => (week.year === currentYear && week.week <= currentWeek) || week.year < currentYear);

        const serviceLevelData = weeklySales.map((week, index) => {
            const weeksBefore = weeklySales.slice(Math.max(0, index - 52), index);

            const anomalies = weeksBefore.map(week => (week.sales.anomaly === null ? week.sales.detectedAnomaly : week.sales.anomaly))
            const serviceLevel = anomalies.length > 0 ? 1 - (anomalies.filter(anomaly => anomaly === true).length / anomalies.length) : 0;

            return {
                week: week.week,
                year: week.year,
                serviceLevel: serviceLevel,
            };
        });

        const serviceLevelDataLast52Weeks = serviceLevelData.filter(week => week.year > firstYear || (week.year === firstYear && week.week >= firstWeek));

        return serviceLevelDataLast52Weeks;
    }, [weeklySales]);


    const getServiceLevelLabel = (serviceLevelData: typeof weeklyServiceLevel) => {
        if (!serviceLevelData || serviceLevelData.length === 0) {
            return { color: theme.palette.text.secondary, label: 'No data available' };
        }

        const startServiceLevel = serviceLevelData[0]?.serviceLevel ?? 0;
        const endServiceLevel = serviceLevelData[serviceLevelData.length - 1]?.serviceLevel ?? 0;
        const serviceLevelChange = (endServiceLevel - startServiceLevel) * 100;


        if (serviceLevelChange > 0) return { color: theme.palette.success.main, label: '+' + serviceLevelChange.toFixed(0) + '% since last year' };
        if (serviceLevelChange === 0) return { color: theme.palette.info.main, label: 'no change' };
        if (serviceLevelChange < 0) return { color: theme.palette.error.main, label: serviceLevelChange.toFixed(0) + '% since last year' };
        return { color: theme.palette.text.secondary, label: serviceLevelChange.toFixed(0) + '%' };
    }

    return (
        <Stack direction="column" spacing={0} sx={{ flex: 1, backgroundColor: "#F2F9FF", borderRadius: 2, p: 3 }}>

            <Stack direction="column" flex={1} spacing={1}>
                <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" color="text.primary">Service Level</Typography>
                    <Typography variant="body2" color="text.secondary">% of Weeks in stock over the last 52 weeks</Typography>
                </Stack>

                {weeklyServiceLevel.length > 0 && weeklyServiceLevel[weeklyServiceLevel.length - 1]?.serviceLevel !== undefined && (
                    <Stack direction="row" spacing={1} justifyContent="flex-start" alignItems="center">
                        <Typography variant="h6" color="text.primary">
                            {`${(weeklyServiceLevel[weeklyServiceLevel.length - 1].serviceLevel * 100).toFixed(0)}% `}
                        </Typography>

                        {(() => {
                            const label = getServiceLevelLabel(weeklyServiceLevel);
                            return (
                                <div
                                    style={{
                                        backgroundColor: addTransparency(label.color, 0.2),
                                        padding: '2px 8px',
                                        borderRadius: '100px',
                                        color: label.color,
                                    }}
                                >
                                    <Typography variant="caption" color="inherit">
                                        {label.label}
                                    </Typography>
                                </div>
                            );
                        })()}
                    </Stack>
                )}
            </Stack>

            <ResponsiveContainer width="100%" height={80}>
                <ComposedChart
                    data={weeklyServiceLevel} margin={{ top: 8, right: 0, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="0 0" stroke={theme.palette.divider} vertical={false} />


                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        tickFormatter={(value) => (value * 100).toFixed(0) + '%'}
                        fontFamily={theme.typography.fontFamily}
                        fontSize={10}
                        tickLine={false}
                        axisLine={false}
                        width={34}
                        domain={[0, 1]}
                    />

                    <Tooltip content={(props: any) => <CustomTooltipContent value={props.payload?.[0]?.value as number} />} />

                    <Area
                        yAxisId="right"
                        type='monotone'
                        dataKey="serviceLevel"
                        name="Service Level"
                        stroke={theme.palette.primary.main}
                        strokeWidth={3}
                        fill="url(#splitColor)"
                        dot={(props: any) => {
                            const index = props.index;
                            if (index === weeklyServiceLevel.length - 1) {
                                return (<circle
                                    cx={props.cx}
                                    cy={props.cy}
                                    r={5}
                                    fill={theme.palette.primary.main}
                                    stroke="#fff"
                                    strokeWidth={2}
                                />)
                            }
                            return <g />;
                        }}
                        isAnimationActive={false}
                    />

                    <defs>
                        <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0" stopColor={theme.palette.primary.main} stopOpacity={0.2} />
                            <stop offset="1" stopColor={theme.palette.primary.main} stopOpacity={0.1} />
                        </linearGradient>
                    </defs>
                </ComposedChart>
            </ResponsiveContainer>
        </Stack>

    );
};


const CustomTooltipContent = ({ value }: { value: number }) => {
    const theme = useTheme();

    return (
        <Stack direction="row" spacing={1} sx={{ backgroundColor: theme.palette.background.paper, p: 2, borderRadius: 2 }}>
            <Typography variant="body2" color="text.secondary">Service Level</Typography>

            {value !== undefined && value !== null && !isNaN(value) && (
                <Typography variant="body2" color="text.primary">
                    {(value * 100).toFixed(0)} %
                </Typography>
            )}
        </Stack>
    );
};